import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { FileText, Mail, ClipboardList, Briefcase, ArrowUpRight, Layers, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  proposals: { icon: FileText, color: 'bg-blue-100 text-blue-700', label: 'Proposals & Quotes', link: '/sales/proposals' },
  emails: { icon: Mail, color: 'bg-purple-100 text-purple-700', label: 'Email Templates', link: '/email-marketing' },
  forms: { icon: ClipboardList, color: 'bg-emerald-100 text-emerald-700', label: 'Form Templates', link: '/leads/capture-forms' },
  field_jobs: { icon: Briefcase, color: 'bg-amber-100 text-amber-700', label: 'Job Templates', link: '/operations/field-jobs' },
};

export default function TemplateLibraryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/sales/template-library`);
      setData(d);
    } catch (e) {
      console.error('Template library load error:', e?.response?.status);
      setData({ proposals: [], emails: [], forms: [], field_jobs: [], total: 0 });
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="template-library-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center"><Layers className="h-5 w-5 text-indigo-500" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Template Library<PageInfoTooltip pageKey="template-library" /></h1>
          <p className="text-xs text-gray-400">{data.total} templates across proposals, emails, forms, and field jobs</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
          const count = (data[key] || []).length;
          const Icon = cfg.icon;
          return (
            <Card key={key} className="bg-white border-gray-border cursor-pointer hover:border-gray-300 transition-colors"
              onClick={() => navigate(cfg.link)} data-testid={`template-cat-${key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", cfg.color.split(' ')[0])}>
                    <Icon className={cn("h-4 w-4", cfg.color.split(' ')[1])} />
                  </div>
                  <span className="text-xs font-medium text-gray-500">{cfg.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">{count}</span>
                  <ArrowUpRight className="h-4 w-4 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* All Templates */}
      <Tabs defaultValue="proposals">
        <TabsList className="bg-gray-100">
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
            <TabsTrigger key={key} value={key} className="text-xs gap-1.5">
              <cfg.icon className="h-3.5 w-3.5" />{cfg.label} ({(data[key] || []).length})
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
          <TabsContent key={key} value={key}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(data[key] || []).map((t, i) => {
                const Icon = cfg.icon;
                return (
                  <Card key={t.id || i} className="bg-white border-gray-border hover:border-gray-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", cfg.color.split(' ')[0])}>
                          <Icon className={cn("h-5 w-5", cfg.color.split(' ')[1])} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{t.name}</p>
                          <Badge className="text-[9px] bg-gray-100 text-gray-500 mt-1">{t.category || key}</Badge>
                          {t.sections && <p className="text-[10px] text-gray-400 mt-1">{t.sections.length} sections</p>}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="w-full mt-3 text-xs gap-1"
                        onClick={() => navigate(cfg.link)} data-testid={`use-template-${t.id || i}`}>
                        <ArrowUpRight className="h-3 w-3" />Use Template
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
              {(data[key] || []).length === 0 && (
                <div className="col-span-full py-8 text-center">
                  <cfg.icon className="h-12 w-12 mx-auto text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">No {cfg.label.toLowerCase()} yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
